import React, { useState, useEffect } from 'react';
import { CaraLoomLogo } from '../components/brand/CaraLoomLogo';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { SkeletonCard } from '../components/ui/ai-loading';
import {
  Activity, Moon, Sun, Users, Calendar, TrendingUp,
  FileText, AlertTriangle, DollarSign, CheckCircle,
  BarChart3, ArrowUp, ArrowDown, Building2, Star,
  MessageSquare, Shield, Clock, UserCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAnalytics() {
  const { user, token, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [analytics, setAnalytics] = useState(null);
  const [trends, setTrends] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [analyticsRes, trendsRes, rankingsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/analytics/trends?days=14`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/clinician-rankings`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (trendsRes.ok) setTrends(await trendsRes.json());
      if (rankingsRes.ok) setRankings(await rankingsRes.json());
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) => (
    <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500 dark:text-slate-400">{title}</p>
            <p className={`text-3xl font-bold text-${color}-600 dark:text-${color}-400 font-display mt-1`}>
              {value}
            </p>
            {subtitle && (
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">{subtitle}</p>
            )}
          </div>
          <div className={`w-12 h-12 bg-${color}-100 dark:bg-${color}-900/30 rounded-xl flex items-center justify-center`}>
            <Icon className={`w-6 h-6 text-${color}-600 dark:text-${color}-400`} />
          </div>
        </div>
        {trend !== undefined && (
          <div className="flex items-center gap-1 mt-3">
            {trend >= 0 ? (
              <ArrowUp className="w-4 h-4 text-cn-primary" />
            ) : (
              <ArrowDown className="w-4 h-4 text-red-500" />
            )}
            <span className={trend >= 0 ? 'text-cn-primary' : 'text-red-500'}>
              {Math.abs(trend)}%
            </span>
            <span className="text-xs text-slate-400 ml-1">vs last period</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-cn-bg dark:bg-cn-ink-900" data-testid="admin-analytics-loading">
        <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200/50 dark:border-cn-ink-700/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-3">
                <CaraLoomLogo size={30} />
              </div>
            </div>
          </div>
        </nav>
        
        <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="mb-8">
            <div className="h-8 w-64 bg-slate-200 dark:bg-cn-ink-700 rounded-lg animate-pulse mb-2" />
            <div className="h-4 w-48 bg-slate-200 dark:bg-cn-ink-700 rounded animate-pulse" />
          </div>
          
          {/* Skeleton Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-cn-ink-800 rounded-2xl p-6 shadow-lg animate-pulse">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="h-3 w-20 bg-slate-200 dark:bg-cn-ink-700 rounded" />
                    <div className="h-8 w-16 bg-slate-200 dark:bg-cn-ink-700 rounded" />
                    <div className="h-2 w-24 bg-slate-200 dark:bg-cn-ink-700 rounded" />
                  </div>
                  <div className="w-12 h-12 bg-slate-200 dark:bg-cn-ink-700 rounded-xl" />
                </div>
              </div>
            ))}
          </div>
          
          {/* Skeleton Charts */}
          <div className="grid lg:grid-cols-2 gap-6">
            <SkeletonCard className="h-80" />
            <SkeletonCard className="h-80" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cn-bg dark:bg-cn-ink-900" data-testid="admin-analytics">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200/50 dark:border-cn-ink-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <CaraLoomLogo size={30} />
            </Link>

            <div className="flex items-center gap-3">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-cn-ink-800 transition-colors"
              >
                {theme === 'light' ? <Moon className="w-5 h-5 text-slate-600" /> : <Sun className="w-5 h-5 text-yellow-400" />}
              </button>
              <span className="text-sm text-slate-600 dark:text-slate-300">
                Admin: {user?.first_name}
              </span>
              <Button variant="ghost" onClick={logout} className="text-slate-600 dark:text-slate-300">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-display">
              Analytics Dashboard
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">
              Real-time platform metrics and insights
            </p>
          </div>
          <Link to="/admin/dashboard">
            <Button variant="outline" className="rounded-full">
              Back to Admin
            </Button>
          </Link>
        </div>

        {analytics && (
          <div className="space-y-8">
            {/* Key Metrics */}
            <section>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Key Metrics</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard
                  title="Total Users"
                  value={analytics.users.total}
                  subtitle={`${analytics.users.patients} patients, ${analytics.users.clinicians} clinicians`}
                  icon={Users}
                  color="blue"
                />
                <StatCard
                  title="Verified Clinicians"
                  value={analytics.users.verified_clinicians}
                  subtitle="Background checked"
                  icon={UserCheck}
                  color="emerald"
                />
                <StatCard
                  title="Appointments"
                  value={analytics.appointments.total}
                  subtitle={`${analytics.appointments.completion_rate}% completion rate`}
                  icon={Calendar}
                  color="purple"
                />
                <StatCard
                  title="Match Acceptance"
                  value={`${analytics.matches.acceptance_rate}%`}
                  subtitle={`${analytics.matches.accepted} of ${analytics.matches.total} matches`}
                  icon={CheckCircle}
                  color="indigo"
                />
              </div>
            </section>

            {/* Engagement & Risk */}
            <section>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Engagement & Risk</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard
                  title="Daily Check-ins"
                  value={analytics.engagement.total_checkins}
                  subtitle={`${analytics.engagement.recent_checkins_30d} in last 30 days`}
                  icon={MessageSquare}
                  color="cyan"
                />
                <StatCard
                  title="Documents Shared"
                  value={analytics.engagement.documents_shared}
                  subtitle="Secure uploads"
                  icon={FileText}
                  color="amber"
                />
                <StatCard
                  title="High Risk Patients"
                  value={analytics.risk.high_risk_patients}
                  subtitle="Require attention"
                  icon={AlertTriangle}
                  color="red"
                />
                <StatCard
                  title="Demo Requests"
                  value={analytics.business.demo_requests_total}
                  subtitle={`${analytics.business.demo_requests_pending} pending`}
                  icon={Building2}
                  color="violet"
                />
              </div>
            </section>

            {/* Revenue */}
            <section>
              <Card className="bg-gradient-to-br from-cn-sage-500 to-green-600 border-0 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-cn-sage-100">Platform Revenue</p>
                      <p className="text-4xl font-bold font-display mt-1">
                        ${(analytics.business.platform_revenue ?? 0).toLocaleString()}
                      </p>
                      <p className="text-cn-sage-200 text-sm mt-1">
                        Collected fees · ${(analytics.business.gross_payment_volume ?? 0).toLocaleString()} gross volume
                      </p>
                    </div>
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                      <DollarSign className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Trends Chart */}
            <section>
              <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-cn-primary" />
                    Activity Trends (Last 14 Days)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64 flex items-end gap-2">
                    {trends.slice(-14).map((day, index) => {
                      const maxValue = Math.max(...trends.map(t => t.check_ins + t.appointments + t.new_users)) || 1;
                      const height = ((day.check_ins + day.appointments + day.new_users) / maxValue) * 100;
                      return (
                        <div key={index} className="flex-1 flex flex-col items-center">
                          <div
                            className="w-full bg-gradient-to-t from-cn-primary to-cn-sage-500 rounded-t-sm transition-all hover:from-cn-primary hover:to-cn-sage-600"
                            style={{ height: `${Math.max(height, 5)}%` }}
                            title={`${day.date}: ${day.new_users} users, ${day.check_ins} check-ins, ${day.appointments} appointments`}
                          />
                          <span className="text-xs text-slate-400 mt-2 transform -rotate-45 origin-left">
                            {day.date.slice(5)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-center gap-6 mt-4">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-cn-primary rounded" />
                      <span className="text-sm text-slate-600 dark:text-slate-400">Combined Activity</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Top Clinicians */}
            <section>
              <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-amber-500" />
                    Top Performing Clinicians
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {rankings.length === 0 ? (
                    <p className="text-center text-slate-500 dark:text-slate-400 py-8">
                      No performance data available yet
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {rankings.slice(0, 5).map((clinician, index) => (
                        <div
                          key={clinician.clinician_id}
                          className="flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-cn-ink-700/50"
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                              index === 0 ? 'bg-amber-500' :
                              index === 1 ? 'bg-slate-400' :
                              index === 2 ? 'bg-amber-700' :
                              'bg-slate-300'
                            }`}>
                              {index + 1}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 dark:text-white">
                                {clinician.name || 'Clinician'}
                              </p>
                              <p className="text-sm text-slate-500 dark:text-slate-400">
                                {clinician.specialties?.join(', ') || 'General Care'}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center gap-1">
                              <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                              <span className="font-bold text-slate-900 dark:text-white">
                                {clinician.overall_rating?.toFixed(1) || 'N/A'}
                              </span>
                            </div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              Score: {clinician.composite_score || 0}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
